import React, { useEffect, useMemo, useState } from "react";
import { Download, Eye, FileText, Wallet } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { downloadPayslipPdf, pdfErrorMessage, PAYSLIP_PASSWORD_HINT } from "@/lib/payslipPdf";
import { MobilePage, GlassCard } from "@/components/mobile/MobilePrimitives";
import PayslipHours from "@/components/app/PayslipHours";
import { AllowanceDetail, DeductionDetail } from "@/components/app/PayslipDetail";

const php = (n) => new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(Number(n || 0));

const fmtDate = (d) => {
  if (!d) return "Pay Date";
  try {
    return new Date(d).toLocaleDateString("en-PH", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return d;
  }
};

function getPayDate(item) {
  return item.pay_date || item.payout_date || item.period_end || item.created_at;
}

function getNetPay(item) {
  return item.net_pay ?? item.total_net ?? item.amount ?? 0;
}

function PayslipModal({ payslip, onClose }) {
  if (!payslip) return null;

  const rows = [
    ["Basic Pay", payslip.basic_pay],
    ["OT Pay", payslip.ot_pay],
    ...[
      ["Rest Day OT Pay", payslip.rest_day_ot_pay],
      ["Special Holiday OT Pay", payslip.special_day_ot_pay],
      ["Regular Holiday OT Pay", payslip.regular_holiday_ot_pay],
    ].filter(([, v]) => Number(v || 0)),
    ["Night Diff", payslip.night_diff_pay],
    ...[
      ["Holiday Pay", payslip.holiday_pay],
      ["Rest Day Pay", payslip.rest_day_pay],
    ].filter(([, v]) => Number(v || 0)),
    ["Hazard Pay", payslip.hazard_pay],
    ...(Number(payslip.minimum_wage_adjustment || 0) ? [["Minimum Wage Adjustment", payslip.minimum_wage_adjustment]] : []),
    ...(payslip.detail ? [] : [["Allowances", Number(payslip.allowances_taxable || 0) + Number(payslip.allowances_nontaxable || 0)]]),
  ];
  const closing = [
    ["Gross Pay", payslip.gross_pay],
    ...(payslip.detail ? [] : [
      ["SSS", payslip.sss_ee],
      ["PhilHealth", payslip.philhealth_ee],
      ["Pag-IBIG", payslip.pagibig_ee],
      ["Tax", payslip.withholding_tax],
      ["Loans", payslip.loans],
    ]),
  ];
  const ending = [
    ["Total deductions", payslip.total_deductions],
    ["Net Pay", payslip.net_pay],
  ];
  const rowCls = "flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3";

  const printPayslip = () => window.print();

  return (
    <div className="fixed inset-0 z-[80] overflow-y-auto bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="mx-auto max-w-md rounded-[30px] bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-sm font-bold text-slate-500">Payslip</div>
            <div className="text-2xl font-black">{fmtDate(getPayDate(payslip))}</div>
          </div>
          <button onClick={onClose} className="rounded-full bg-slate-100 px-3 py-1 text-sm font-bold">Close</button>
        </div>

        <div className="mt-5 rounded-[24px] bg-gradient-to-br from-blue-600 to-cyan-500 p-5 text-white">
          <div className="text-sm text-white/80">Net Pay</div>
          <div className="text-4xl font-black">{php(payslip.net_pay)}</div>
        </div>

        <div className="mt-5 space-y-2">
          {rows.map(([label, value]) => (
            <div key={label} className={rowCls}>
              <span className="text-sm text-slate-500">{label}</span>
              <span className="text-sm font-black">{php(value)}</span>
            </div>
          ))}
          {payslip.detail && <div className="rounded-2xl bg-slate-50 px-4 py-2 text-sm"><AllowanceDetail detail={payslip.detail} /></div>}
          {closing.map(([label, value]) => (
            <div key={label} className={rowCls}>
              <span className="text-sm text-slate-500">{label}</span>
              <span className="text-sm font-black">{php(value)}</span>
            </div>
          ))}
          {payslip.detail && <div className="rounded-2xl bg-slate-50 px-4 py-2 text-sm"><div className="pt-2 text-xs font-bold uppercase text-slate-500">Deductions</div><DeductionDetail detail={payslip.detail} /></div>}
          {ending.map(([label, value]) => (
            <div key={label} className={rowCls}>
              <span className="text-sm text-slate-500">{label}</span>
              <span className="text-sm font-black">{php(value)}</span>
            </div>
          ))}
        </div>

        <PayslipHours block={payslip.hours_block} className="mt-5 rounded-2xl bg-slate-50 p-3" />

        {payslip.company?.payslip_password_protect ? (
          <>
            <button onClick={async () => { try { await downloadPayslipPdf(payslip.id); } catch (e) { toast.error(await pdfErrorMessage(e)); } }} className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-700 px-4 py-4 font-black text-white">
              <Download className="h-5 w-5" />
              Download PDF (password protected)
            </button>
            <div className="mt-3 rounded-2xl bg-slate-50 px-4 py-3 text-xs text-slate-600">{PAYSLIP_PASSWORD_HINT}</div>
          </>
        ) : (
          <button onClick={printPayslip} className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-700 px-4 py-4 font-black text-white">
            <Download className="h-5 w-5" />
            Download / Print PDF
          </button>
        )}
      </div>
    </div>
  );
}

export default function MobilePayslips() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);

  const totalNet = useMemo(() => items.reduce((sum, x) => sum + Number(getNetPay(x) || 0), 0), [items]);

  useEffect(() => {
    api.get("/me/payslips").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);

  const viewPayslip = async (item) => {
    try {
      const r = await api.get(`/me/payslips/${item.id}`);
      setSelected({ ...item, ...(r.data || {}) });
    } catch {
      setSelected(item);
    }
  };

  return (
    <MobilePage>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-bold text-slate-500">My Payroll</div>
          <div className="text-3xl font-black">Payslips</div>
        </div>
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
          <Wallet className="h-6 w-6" />
        </div>
      </div>

      <GlassCard className="bg-gradient-to-br from-blue-600 to-cyan-500 p-5 text-white">
        <div className="text-sm text-white/80">Available Payslips</div>
        <div className="mt-1 text-4xl font-black">{items.length}</div>
        <div className="mt-2 text-sm text-white/80">Total displayed net pay: {php(totalNet)}</div>
      </GlassCard>

      <div className="space-y-3">
        {items.length === 0 && (
          <GlassCard className="p-6 text-center">
            <FileText className="mx-auto h-10 w-10 text-slate-400" />
            <div className="mt-3 font-black">No payslips yet</div>
            <div className="text-sm text-slate-500">Released payslips will appear here.</div>
          </GlassCard>
        )}

        {items.map((item) => (
          <GlassCard key={item.id} className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Pay Date</div>
                <div className="text-lg font-black">{fmtDate(getPayDate(item))}</div>
              </div>
              <div className="text-right">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Net</div>
                <div className="text-xl font-black text-blue-700">{php(getNetPay(item))}</div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <button onClick={() => viewPayslip(item)} className="flex items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 py-3 text-sm font-black text-slate-700">
                <Eye className="h-4 w-4" />
                View
              </button>
              <button onClick={() => viewPayslip(item)} className="flex items-center justify-center gap-2 rounded-2xl bg-blue-700 px-4 py-3 text-sm font-black text-white">
                <Download className="h-4 w-4" />
                Download
              </button>
            </div>
          </GlassCard>
        ))}
      </div>

      <PayslipModal payslip={selected} onClose={() => setSelected(null)} />
    </MobilePage>
  );
}
